import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  addToCart,
  getAvailableCartItems,
  getAvailableUserDetail,
  removeFromCart,
  updateOrderDetails,
} from '../redux/addCart/cartSlice';
import { getImagePath } from '../constants/defaultValues';
import { getCurrentUser } from '../redux/auth/authSlice';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  FaArrowRight,
  FaArrowLeft,
  FaShoppingBasket,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaUserEdit,
  FaTrashAlt,
  FaMinus,
  FaPlus,
} from 'react-icons/fa';
import EmptyCart from './common/EmptyCart';

const CartSummary = ({ showBucketDetails, setShowBucketDetails, emptyButton ,darkMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Central Hook for Colors
  const {
    cardBg,
    textColor,
    borderColor,
    headerBg,
    isDark,
    contentBg,
    subTextColor,
  } = useThemeColors(darkMode);

  const cartItems = useSelector(getAvailableCartItems);
  const userDetail = useSelector(getAvailableUserDetail);
  const user = useSelector(getCurrentUser);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.shopPrice * item.quantity,
    0
  );
  const finalTotal = totalPrice;

  useEffect(() => {
    if (user) {
      dispatch(
        updateOrderDetails({
          ...userDetail,
          customerName: user.name || '',
          customerMobileNumber: user.mobileNumber || '',
          customerAddress: user.customerAddress || '',
        })
      );
    }
  }, [user, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateOrderDetails({ ...userDetail, [name]: value }));
  };

  // Dynamic Input Style
  const inputStyle = {
    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
    color: textColor,
    border: `1px solid ${borderColor}`,
    borderRadius: '12px',
    padding: '12px 15px',
    fontSize: '14px',
    transition: '0.3s ease',
  };

  const handleAction = () => {
    if (!showBucketDetails) {
      navigate('/cartItemsDetails');
      setShowBucketDetails(true);
    } else {
      navigate('/');
      setShowBucketDetails(false);
    }
  };

  return (
    <div
      className="card rounded border-0 shadow-lg sticky-top"
      style={{
        backgroundColor: contentBg,
        color: textColor,
        borderRadius: '20px',
        transition: '0.3s',
        top: '100px',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <div
        className="card-header py-3 border-0 d-flex justify-content-between align-items-center"
        style={{ backgroundColor: headerBg }}
      >
        <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
          {showBucketDetails ? (
            <FaUserEdit className="text-danger" />
          ) : (
            <FaShoppingBasket className="text-danger" />
          )}
          {showBucketDetails ? 'Delivery Details' : 'Your Basket'}
        </h6>
        {!showBucketDetails && (
          <span className="badge bg-danger rounded-pill px-3">
            {totalItems} Items
          </span>
        )}
      </div>

      <div
        className="card-body p-0 position-relative"
        style={{ height: '50vh', overflow: 'hidden' }}
      >
        {!showBucketDetails ? (
          <div className="p-3 h-100" style={{ overflowY: 'auto' }}>
            {cartItems.length === 0 ? (
              <EmptyCart darkMode={isDark} emptyButton={emptyButton} />
            ) : (
              cartItems.map((item) => (
                <div
                  key={item.id}
                  className="card mb-3 rounded border-0 shadow-sm"
                  style={{ backgroundColor: cardBg }}
                >
                  <div className="card-body p-2 d-flex align-items-center gap-3">
                    {item.image?.fileToken ? (
                      <img
                        src={`${getImagePath}${item.image.fileToken}`}
                        alt={item.name || 'product'}
                        className="rounded-3"
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-3 bg-light d-flex align-items-center justify-content-center"
                        style={{
                          width: '60px',
                          height: '60px',
                          fontSize: '10px',
                          color: '#ccc',
                        }}
                      >
                        No Image
                      </div>
                    )}
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex justify-content-between">
                        <span
                          className="fw-bold small text-truncate pe-2"
                          style={{ color: textColor }}
                        >
                          {item.name}
                        </span>
                        <span className="text-danger fw-bold small">
                          Rs {(item.shopPrice * item.quantity).toLocaleString()}
                        </span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mt-1">
                        <div
                          className="d-flex align-items-center rounded border"
                          style={{
                            backgroundColor: isDark
                              ? 'rgba(255,255,255,0.05)'
                              : '#f8f9fa',
                            borderColor: borderColor,
                          }}
                        >
                          <button
                            className="btn btn-sm p-1 text-danger rounded"
                            onClick={() =>
                              dispatch(removeFromCart({ itemId: item.id }))
                            }
                          >
                            {item.quantity === 1 ? (
                              <FaTrashAlt size={12} />
                            ) : (
                              <FaMinus size={12} />
                            )}
                          </button>
                          <span
                            className="small fw-bold px-2"
                            style={{
                              color: textColor,
                            }}
                          >
                            {item.quantity}
                          </span>
                          <button
                            className="btn btn-sm p-1 text-success rounded"
                            onClick={() =>
                              dispatch(addToCart({ ...item, quantity: 1 }))
                            }
                          >
                            <FaPlus size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* CHECKOUT VIEW */
          <div className="p-4 h-100" style={{ overflowY: 'auto' }}>
            <div className="row g-3">
              <div className="col-12">
                <label
                  className="form-label small fw-bold"
                  style={{ color: subTextColor }}
                >
                  Full Name
                </label>
                <input
                  className="form-control"
                  name="customerName"
                  value={userDetail?.customerName || ''}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="Enter your name"
                />
              </div>
              <div className="col-12">
                <label
                  className="form-label small fw-bold"
                  style={{ color: subTextColor }}
                >
                  Delivery Address
                </label>
                <input
                  className="form-control"
                  name="customerAddress"
                  value={userDetail?.customerAddress || ''}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="Street, House No, Area"
                />
              </div>
              <div className="col-12">
                <label
                  className="form-label small fw-bold"
                  style={{ color: subTextColor }}
                >
                  Phone Number
                </label>
                <div className="input-group">
                  <span className="input-group-text border-0 bg-danger text-white rounded-start-pill small px-3">
                    +92
                  </span>
                  <input
                    className="form-control"
                    name="customerMobileNumber"
                    value={userDetail?.customerMobileNumber || ''}
                    onChange={handleInputChange}
                    style={{
                      ...inputStyle,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    placeholder="3XXXXXXXXX"
                  />
                </div>
              </div>
            </div>

            <div
              className="mt-4 p-3 rounded-4"
              style={{
                backgroundColor: headerBg,
                border: `1px solid ${borderColor}`,
              }}
            >
              <div className="d-flex justify-content-between small mb-2 opacity-75">
                <span>Items Subtotal:</span>
                <span className="fw-bold">
                  Rs {totalPrice.toLocaleString()}
                </span>
              </div>
              <div
                className="d-flex justify-content-between fw-bold fs-5 border-top pt-2 mt-2"
                style={{ borderColor: borderColor }}
              >
                <span>Total:</span>
                <span className="text-danger">
                  Rs {finalTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Action Button */}
      <div className="card-footer border-0 p-3 bg-transparent">
        <button
          className={`btn ${
            showBucketDetails ? 'btn-outline-secondary' : 'btn-danger'
          } w-100 rounded py-3 shadow-sm fw-bold d-flex justify-content-between align-items-center px-4`}
          onClick={handleAction}
          disabled={!showBucketDetails && cartItems.length === 0}
          style={{ transition: '0.3s' }}
        >
          {showBucketDetails ? (
            <>
              <FaArrowLeft />
              <span>Back to Basket</span>
              <span style={{ width: '20px' }}></span>
            </>
          ) : (
            <>
              <span>Checkout</span>
              <div className="d-flex align-items-center gap-2">
                <span>Rs {finalTotal.toLocaleString()}</span>
                <FaArrowRight />
              </div>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CartSummary;
